import Button from '@rio-cloud/rio-uikit/Button';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import {
  SearchContainer,
  StyledButtons,
} from '@/components/atom/QuestionsListAssetTreeSummary/QuestionsListAssetTreeSummary.styles';
import SearchField from '@/components/atom/SearchField/SearchField';
import useRovingTabIndex from '@/hooks/useRovingTabIndex';
import { TFaqIconStat } from '@/store/faq/types';
import { selectIsSearching, setIsSearching } from '@/store/search/searchSlice';

type QuestionsListAssetTreeSummaryProps = {
  faqIcons: TFaqIconStat[];
  onFilterSelect: (filter: string) => void;
  onSearch: (value: string) => void;
  activeIcon: string | null;
  isOnboarding: boolean;
};

const TOOLBAR_LABEL = 'chatPage.servicesSection.exemplary_questions.toolbarLabel';

const QuestionsListAssetTreeSummary = ({
  faqIcons,
  activeIcon,
  onSearch,
  onFilterSelect,
  isOnboarding,
}: QuestionsListAssetTreeSummaryProps) => {
  const dispatch = useDispatch();
  const isSearching = useSelector(selectIsSearching);
  const intl = useIntl();

  const isSmallScreen = useMediaQuery({ query: '(max-width: 47.5rem)' });

  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreSearchFocusRef = useRef(false);

  /** Determines if a filter at a given index is disabled (all disabled during onboarding). */
  const isItemDisabled = useCallback((_index: number) => isOnboarding, [isOnboarding]);

  const handleFilterActivate = useCallback(
    (index: number) => {
      const filter = faqIcons[index];
      if (filter) {
        onFilterSelect(filter.type === activeIcon ? '' : filter.type);
      }
    },
    [faqIcons, activeIcon, onFilterSelect],
  );

  const { handleKeyDown, getTabIndex, setItemRef } = useRovingTabIndex({
    itemCount: faqIcons.length,
    orientation: isSmallScreen ? 'vertical' : 'horizontal',
    isItemDisabled,
    onActivate: handleFilterActivate,
  });

  const handleSearchEscape = useCallback(() => {
    // INFO: The Search toggle is unmounted while searching, so focus cannot be restored here —
    // the effect below does it once the toggle is back in the DOM.
    shouldRestoreSearchFocusRef.current = true;
    dispatch(setIsSearching(false));
  }, [dispatch]);

  useEffect(() => {
    // INFO: isSearching going false means the Search toggle has remounted. The ref gate keeps this
    // from stealing focus when search is reset for other reasons (panel closed, question selected),
    // which move focus to their own targets.
    if (!isSearching && shouldRestoreSearchFocusRef.current) {
      shouldRestoreSearchFocusRef.current = false;
      searchToggleRef.current?.focus();
    }
  }, [isSearching]);

  const toolbarLabel = intl.formatMessage({
    id: TOOLBAR_LABEL,
    defaultMessage: 'Category filters',
  });

  const orientation = isSmallScreen ? 'vertical' : 'horizontal';

  const buttonToolbar = (
    <div role="toolbar" aria-label={toolbarLabel} aria-orientation={orientation} onKeyDown={handleKeyDown}>
      <div className={isSmallScreen ? 'btn-group-vertical' : 'btn-group'}>
        {faqIcons.map(({ icon, type, tooltipText }: TFaqIconStat, index: number) => (
          <Button
            data-testid={`filter-${type}`}
            className={isSmallScreen ? 'button-toolbar-group' : ''}
            key={icon}
            iconName={`rioglyph-${icon}`}
            active={icon === activeIcon}
            onClick={() => onFilterSelect(type === activeIcon ? '' : type)}
            disabled={isOnboarding}
            tabIndex={isOnboarding ? -1 : getTabIndex(index)}
            ref={setItemRef(index)}
          >
            <FormattedMessage id={tooltipText} />
          </Button>
        ))}
      </div>
    </div>
  );

  const buttonSearch = (
    <SearchContainer>
      {!isSearching && (
        <Button
          ref={searchToggleRef}
          className={isSmallScreen ? 'button-toolbar-group' : ''}
          iconName="rioglyph-search-list"
          onClick={() => dispatch(setIsSearching(true))}
          disabled={isOnboarding}
          data-testid="search-example-questions"
        >
          <FormattedMessage id="chatPage.servicesSection.search" />
        </Button>
      )}

      {isSearching && (
        <SearchField onSearch={(value) => onSearch(value)} shouldAutoFocus onEscape={handleSearchEscape} />
      )}
    </SearchContainer>
  );

  return (
    <StyledButtons>
      <div className="buttons-wrapper">
        {buttonToolbar}
        {buttonSearch}
      </div>
    </StyledButtons>
  );
};

export default QuestionsListAssetTreeSummary;
